import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { CheckCircleIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useEvent } from '../../contexts/EventContext';
import { EventType } from '../../types';
export function Onboarding() {
  const {
    completeOnboarding,
    user
  } = useAuth();
  const {
    allEvents,
    switchEvent
  } = useEvent();
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState({
    name: user?.name || '',
    company: '',
    role: '',
    bio: '',
    interests: [] as string[],
    defaultEvent: 'muscle' as EventType
  });
  const interestOptions = ['Fitness Equipment', 'Nutrition', 'Wellness', 'Technology', 'Business Development', 'Franchising', 'Bodybuilding', 'Group Fitness'];
  const handleComplete = () => {
    // Switch to the selected event before completing onboarding
    switchEvent(profile.defaultEvent);
    completeOnboarding(profile);
  };
  const toggleInterest = (interest: string) => {
    setProfile(prev => ({
      ...prev,
      interests: prev.interests.includes(interest) ? prev.interests.filter(i => i !== interest) : [...prev.interests, interest]
    }));
  };
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-3253">
      <div className="w-full max-w-2xl" data-id="element-3254">
        <div className="text-center mb-8" data-id="element-3255">
          <h1 className="text-4xl font-bold text-gray-900 mb-2 font-gotham" data-id="element-3256">
            Welcome to the IEG app
          </h1>
          <p className="text-gray-600 font-gotham" data-id="element-3257">Let's set up your profile</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8" data-id="element-3258">
          {[1, 2, 3].map(num => <div key={num} className="flex items-center" data-id="element-3259">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold font-gotham ${step >= num ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-3260">
                {step > num ? <CheckCircleIcon className="w-6 h-6" data-id="element-3261" /> : num}
              </div>
              {num < 3 && <div className={`w-12 h-1 mx-2 ${step > num ? 'bg-gray-900' : 'bg-gray-200'}`} data-id="element-3262" />}
            </div>)}
        </div>

        <Card padding="lg" data-id="element-3263">
          {/* Step 1: Basic Info */}
          {step === 1 && <div className="space-y-4" data-id="element-3264">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-gotham" data-id="element-3265">
                Basic Information
              </h2>

              <Input label="Full Name" placeholder="John Doe" value={profile.name} onChange={e => setProfile({
            ...profile,
            name: e.target.value
          })} required data-id="element-3266" />

              <Input label="Company" placeholder="Your Company" value={profile.company} onChange={e => setProfile({
            ...profile,
            company: e.target.value
          })} data-id="element-3267" />

              <Input label="Role" placeholder="Your Role" value={profile.role} onChange={e => setProfile({
            ...profile,
            role: e.target.value
          })} data-id="element-3268" />

              <div data-id="element-3269">
                <label className="block text-sm font-medium text-gray-700 mb-1 font-gotham" data-id="element-3270">
                  Bio
                </label>
                <textarea className="w-full px-4 py-2 border border-gray-300 rounded-lg font-gotham focus:outline-none focus:ring-2 focus:ring-gray-900 transition-all resize-none" rows={4} placeholder="Tell us about yourself..." value={profile.bio} onChange={e => setProfile({
              ...profile,
              bio: e.target.value
            })} data-id="element-3271" />
              </div>

              <Button variant="primary" fullWidth onClick={() => setStep(2)} disabled={!profile.name} data-id="element-3272">
                Continue
              </Button>
            </div>}

          {/* Step 2: Interests */}
          {step === 2 && <div className="space-y-4" data-id="element-3273">
              <h2 className="text-2xl font-bold text-gray-900 mb-2 font-gotham" data-id="element-3274">
                Your Interests
              </h2>
              <p className="text-gray-600 font-gotham mb-4" data-id="element-3275">
                Select topics you're interested in (optional)
              </p>

              <div className="grid grid-cols-2 gap-3" data-id="element-3276">
                {interestOptions.map(interest => <button key={interest} onClick={() => toggleInterest(interest)} className={`p-3 rounded-lg border-2 font-medium font-gotham transition-all ${profile.interests.includes(interest) ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 text-gray-700 hover:border-gray-400'}`} data-id="element-3277">
                    {interest}
                  </button>)}
              </div>

              <div className="flex gap-3 mt-6" data-id="element-3278">
                <Button variant="outline" fullWidth onClick={() => setStep(1)} data-id="element-3279">
                  Back
                </Button>
                <Button variant="primary" fullWidth onClick={() => setStep(3)} data-id="element-3280">
                  Continue
                </Button>
              </div>
            </div>}

          {/* Step 3: Select Event */}
          {step === 3 && <div className="space-y-4" data-id="element-3281">
              <h2 className="text-2xl font-bold text-gray-900 mb-2 font-gotham" data-id="element-3282">
                Choose Your Event
              </h2>
              <p className="text-gray-600 font-gotham mb-4" data-id="element-3283">
                Select your default event (you can switch anytime)
              </p>

              <div className="space-y-3" data-id="element-3284">
                {allEvents.map(event => <button key={event.id} onClick={() => setProfile({
              ...profile,
              defaultEvent: event.id
            })} className={`w-full text-left p-4 rounded-lg border-2 transition-all ${profile.defaultEvent === event.id ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3285">
                    <div className="flex items-start justify-between" data-id="element-3286">
                      <div data-id="element-3287">
                        <h3 className="font-bold text-gray-900 font-gotham mb-1" data-id="element-3288">
                          {event.name}
                        </h3>
                        <p className="text-sm text-gray-600 font-gotham mb-2" data-id="element-3289">
                          {event.description}
                        </p>
                        <p className="text-xs text-gray-500 font-gotham" data-id="element-3290">
                          {event.dates}
                        </p>
                      </div>
                      {profile.defaultEvent === event.id && <CheckCircleIcon className="w-6 h-6 text-gray-900 flex-shrink-0" data-id="element-3291" />}
                    </div>
                  </button>)}
              </div>

              <div className="flex gap-3 mt-6" data-id="element-3292">
                <Button variant="outline" fullWidth onClick={() => setStep(2)} data-id="element-3293">
                  Back
                </Button>
                <Button variant="primary" fullWidth onClick={handleComplete} data-id="element-3294">
                  Get Started
                </Button>
              </div>
            </div>}
        </Card>
      </div>
    </div>;
}